import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IntegrationCard } from './IntegrationCard'
import { LiveIntegrationsSection } from './LiveIntegrationsSection'
import { CustomVariablesSection } from './CustomVariablesSection'
import { Plug, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { Project, Integration } from '@/types/conversations'

interface IntegrationsTabProps {
  project: Project
}

// All available integration types from the backend
const INTEGRATION_TYPES = [
  'openai',
  'anthropic',
  'google_gemini',
  'sendgrid',
  'stripe',
  'resend',
  'twilio',
  'aws_s3',
  'google_oauth',
  'github_oauth',
  'samsara',
  'wex',
  'here_maps',
  'google_maps',
  'telegram',
  'instagram',
  'whatsapp',
  'youtube'
]

// Category definitions with integrations
const INTEGRATION_CATEGORIES = {
  ai: {
    label: 'AI',
    integrations: ['openai', 'anthropic', 'google_gemini']
  },
  email: {
    label: 'Email',
    integrations: ['sendgrid', 'resend']
  },
  communication: {
    label: 'Communication',
    integrations: ['twilio', 'telegram', 'whatsapp']
  },
  social: {
    label: 'Social',
    integrations: ['instagram', 'youtube']
  },
  payments: {
    label: 'Payments',
    integrations: ['stripe']
  },
  cloud: {
    label: 'Cloud',
    integrations: ['aws_s3']
  },
  auth: {
    label: 'Auth',
    integrations: ['google_oauth', 'github_oauth']
  },
  fleet: {
    label: 'Fleet',
    integrations: ['samsara', 'wex']
  },
  maps: {
    label: 'Maps',
    integrations: ['here_maps', 'google_maps']
  }
} as const

type CategoryKey = keyof typeof INTEGRATION_CATEGORIES

// Map integration type to display name for search
const INTEGRATION_NAMES: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google_gemini: 'Google Gemini',
  sendgrid: 'SendGrid',
  stripe: 'Stripe',
  resend: 'Resend',
  twilio: 'Twilio',
  aws_s3: 'AWS S3',
  google_oauth: 'Google OAuth',
  github_oauth: 'GitHub OAuth',
  samsara: 'Samsara',
  wex: 'WEX',
  here_maps: 'HERE Maps',
  google_maps: 'Google Maps',
  telegram: 'Telegram',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  youtube: 'YouTube'
}

type Environment = 'sandbox' | 'live'

export function IntegrationsTab({ project }: IntegrationsTabProps) {
  const { t } = useTranslation('conversations')
  const [expandedIntegration, setExpandedIntegration] = useState<string | null>(null)
  const [activeEnvironment, setActiveEnvironment] = useState<Environment>('sandbox')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<CategoryKey | 'all'>('all')

  // Get all integrations (both project and organization)
  const projectIntegrations = project.integrations || []
  const orgIntegrations = project.organization_integrations || []

  // Map of integration_type to integration for quick lookup
  const projectIntegrationMap = new Map<string, Integration>()
  projectIntegrations.forEach(integration => {
    projectIntegrationMap.set(integration.integration_type, integration)
  })

  const orgIntegrationMap = new Map<string, Integration>()
  orgIntegrations.forEach(integration => {
    orgIntegrationMap.set(integration.integration_type, integration)
  })

  // An integration is only "using org" if the project explicitly doesn't have one
  // After disconnecting a project integration, it should show as "Not Connected"
  // not automatically fall back to "Using Org"
  const isUsingOrgIntegration = (_integrationType: string) => {
    return false // Never show "Using Org" - integrations must be explicitly imported
  }

  const handleToggleExpand = (integrationType: string) => {
    setExpandedIntegration(prev => prev === integrationType ? null : integrationType)
  }

  // Filter integrations based on search and category
  const filteredIntegrations = INTEGRATION_TYPES.filter(type => {
    // Search filter
    const name = INTEGRATION_NAMES[type] || type
    const matchesSearch = searchQuery === '' ||
      name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      type.toLowerCase().includes(searchQuery.toLowerCase())

    // Category filter
    const matchesCategory = selectedCategory === 'all' ||
      (INTEGRATION_CATEGORIES[selectedCategory].integrations as readonly string[]).includes(type)

    return matchesSearch && matchesCategory
  })

  return (
    <div className="p-6 bg-card">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-foreground">{t('integrations.title')}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('integrations.description')}
          </p>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-3">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('integrations.search.placeholder', 'Search integrations...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Category Filter */}
          <Select
            value={selectedCategory}
            onValueChange={(value) => setSelectedCategory(value as CategoryKey | 'all')}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t('integrations.categories.all', 'All')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('integrations.categories.all', 'All')}</SelectItem>
              {Object.entries(INTEGRATION_CATEGORIES).map(([key, { label }]) => (
                <SelectItem key={key} value={key}>
                  {t(`integrations.categories.${key}`, label)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Environment Tabs */}
        <div className="flex items-center justify-between border-b border-border">
          <div className="flex">
            <button
              onClick={() => setActiveEnvironment('sandbox')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeEnvironment === 'sandbox'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('integrations.tabs.sandbox')}
            </button>
            <button
              onClick={() => setActiveEnvironment('live')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeEnvironment === 'live'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('integrations.tabs.live')}
            </button>
          </div>
        </div>

        {/* Content based on active environment */}
        {activeEnvironment === 'sandbox' ? (
          <div className="space-y-3">
            {/* Custom Secrets Section */}
            <CustomVariablesSection
              projectId={project.id}
              variables={project.custom_sandbox_variables || []}
              isLive={false}
            />

            {/* Separator */}
            {(project.custom_sandbox_variables || []).length > 0 && (
              <div className="relative py-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-card px-4 text-sm text-muted-foreground">
                    {t('integrations.separator')}
                  </span>
                </div>
              </div>
            )}

            {/* Empty Search State */}
            {filteredIntegrations.length === 0 && (
              <div className="flex items-center justify-center h-32">
                <div className="text-center space-y-2">
                  <Search className="h-8 w-8 text-muted-foreground mx-auto" />
                  <p className="text-sm text-muted-foreground">
                    {t('integrations.search.noResults', 'No integrations found')}
                  </p>
                </div>
              </div>
            )}

            {filteredIntegrations.map(integrationType => {
              const projectIntegration = projectIntegrationMap.get(integrationType)
              const orgIntegration = orgIntegrationMap.get(integrationType)
              const isExpanded = expandedIntegration === integrationType
              const usingOrgIntegration = isUsingOrgIntegration(integrationType)

              return (
                <IntegrationCard
                  key={integrationType}
                  integrationType={integrationType}
                  projectIntegration={projectIntegration}
                  orgIntegration={orgIntegration}
                  isExpanded={isExpanded}
                  onToggleExpand={() => handleToggleExpand(integrationType)}
                  usingOrgIntegration={usingOrgIntegration}
                  projectId={project.id}
                  environment={activeEnvironment}
                  project={project}
                />
              )
            })}
          </div>
        ) : (
          <div className="space-y-3">
            {/* Custom Secrets Section for Live */}
            <CustomVariablesSection
              projectId={project.id}
              variables={project.custom_live_variables || []}
              isLive={true}
            />

            {/* Separator */}
            {(project.custom_live_variables || []).length > 0 && (
              <div className="relative py-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-card px-4 text-sm text-muted-foreground">
                    {t('integrations.separator')}
                  </span>
                </div>
              </div>
            )}

            <LiveIntegrationsSection
              key={`live-integrations-${project.integrations.map(i => `${i.id}-${i.has_live_variables}`).join('-')}`}
              project={project}
            />
          </div>
        )}

        {/* Empty State (shouldn't show since we always show all types) */}
        {INTEGRATION_TYPES.length === 0 && (
          <div className="flex items-center justify-center h-48">
            <div className="text-center space-y-4">
              <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                <Plug className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-sm mb-1 text-foreground">{t('integrations.empty.title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('integrations.empty.description')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
